// Space Designer (Phase 36) — dedicated manage page for the shared custom-SVG
// library. All assets are space-shared (no private/shared toggle). Backed by the
// CRM /space/svg-assets endpoints. "Add to canvas" imports a shared asset into the
// local designer library so it appears in the Library sidebar.
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Cloud, Upload, Trash2, Pencil, Plus, ArrowLeft } from "lucide-react";
import {
  useSpaceSvgAssets,
  useCreateCloudSvg,
  useRenameSpaceSvg,
  useDeleteSpaceSvg,
} from "./lib/use-space-svg";
import { fileToParsedSvg } from "./lib/svg-loader";
import { useSvgLibrary } from "./lib/svg-library";

export default function SvgLibraryPage() {
  const { data: assets = [], isLoading } = useSpaceSvgAssets();
  const createMut = useCreateCloudSvg();
  const renameMut = useRenameSpaceSvg();
  const deleteMut = useDeleteSpaceSvg();
  const addLocal = useSvgLibrary((s) => s.addAsset);

  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? assets.filter((a) => a.name.toLowerCase().includes(q)) : assets;
  }, [assets, search]);

  async function onUpload(files: FileList | null) {
    if (!files?.length) return;
    let ok = 0;
    for (const f of Array.from(files)) {
      try {
        const parsed = await fileToParsedSvg(f);
        await createMut.mutateAsync({ name: parsed.name, markup: parsed.inner, width: parsed.width, height: parsed.height });
        ok++;
      } catch (e) {
        toast.error(`${f.name}: ${e instanceof Error ? e.message : "failed"}`);
      }
    }
    if (ok) toast.success(`Uploaded ${ok} SVG${ok === 1 ? "" : "s"} to the shared library`);
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Cloud className="w-6 h-6 text-primary" /> Shared SVG library
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Custom shapes uploaded here are shared with everyone in your space and can be dropped onto any floor design.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link href="/inventory/space/designer"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to designer</Link>
          </Button>
          <label>
            <input type="file" accept=".svg,image/svg+xml" multiple className="hidden"
              onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }} />
            <Button asChild size="sm"><span><Upload className="w-4 h-4 mr-1.5" /> Upload SVG</span></Button>
          </label>
        </div>
      </div>

      <Input placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4 max-w-sm" />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No shared SVGs yet. Upload one to get started.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((a) => (
            <div key={a.id} className="rounded-lg border bg-card p-3 flex flex-col gap-2">
              <div className="aspect-square rounded bg-muted/40 flex items-center justify-center overflow-hidden">
                <svg viewBox={a.viewBox} className="w-full h-full p-2" dangerouslySetInnerHTML={{ __html: a.markup }} />
              </div>
              <div className="text-xs font-medium truncate" title={a.name}>{a.name}</div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs flex-1 gap-1"
                  onClick={() => {
                    addLocal({ name: a.name, inner: a.markup, viewBox: a.viewBox, width: Number(a.width ?? 100), height: Number(a.height ?? 100), palette: [] });
                    toast.success(`"${a.name}" added to the canvas library`);
                  }}>
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7"
                  onClick={async () => {
                    const name = window.prompt("Rename asset", a.name)?.trim();
                    if (!name || name === a.name) return;
                    await renameMut.mutateAsync({ id: a.id, data: { name } });
                  }}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                  onClick={async () => {
                    if (!window.confirm(`Delete "${a.name}"?`)) return;
                    await deleteMut.mutateAsync({ id: a.id });
                    toast.success("Deleted");
                  }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
